import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Image } from '@fluentui/react/lib/Image';
import {
  getImageUrlById,
  transformDateWithLocaleForEditMode,
  removeStringEmoji,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  useIsSmartPageImprovedReviewsEnabled,
  useInstrumentationServiceContext,
  useSiteConfig,
  useCurrentLocale,
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { CircleLogo, proxyReadLogoConfig } from '@bingads-webui-campaign-react/smart-component-image';
import { TooltipWrapper } from '@bingads-webui-campaign-react/smart-component-primitive';

import { REVIEW_CARD_MODE, POSITIVE_REVIEW_TYPE, REVIEW_TYPE } from './constants';

const getStyles = (_, { palette = {} }) => ({
  reviewerPhoto: {
    position: 'absolute',
    borderRadius: 50,
    marginRight: 12,
    width: 40,
    height: 40,
    background: '#C4C4C4',
  },
  headerContent: {
    minHeight: 40,
    marginLeft: 52,
    overflow: 'hidden',
    width: 'calc(100% - 90px)',
  },
  recommendationType: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  reviewerName: {
    fontWeight: 600,
    display: 'inline-block',
    verticalAlign: 'middle',
    maxWidth: '60%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  reviewHeadline: {
    display: 'flex',
  },
  reviewerNameLogo: {
    width: 40,
    height: 40,
    position: 'absolute',
    marginRight: 12,
    borderRadius: '50%',
    MozBorderRadius: '50%',
    WebkitBorderRadius: '50%',
  },
  createdTime: {
    display: 'block',
    color: palette.tertiaryText,
    fontSize: 12,
    marginTop: 2,
  },
  businessName: {
    display: 'inline-block',
    verticalAlign: 'middle',
    fontWeight: 600,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
  reviewText: {
    display: 'block',
    marginTop: 12,
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
  [REVIEW_CARD_MODE.EDIT_MODE]: {
    '& .card': {
      position: 'relative',
    },
  },
  [REVIEW_CARD_MODE.VIEW_MODE]: {
    marginBottom: 60,
    '& .card': {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      padding: '15px 0px',
      width: 350,
      height: 185,
      margin: '0 15px',
      background: palette.cardBackground,
      boxShadow: '0px 1.6px 3.6px rgba(0, 0, 0, 0.132), 0px 0.3px 0.9px rgba(0, 0, 0, 0.108)',
      borderRadius: 2,
      '& .review-headline': {
        color: palette.secondaryText,
        margin: '0px 20px 10px 20px',
      },
      '& .review-text': {
        color: palette.secondaryText,
        margin: '0px 11px 0px 20px',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4,
        lineHeight: 1.4,
        fontSize: '14px',
      },
    },
  },
  fbIcon: {
    color: '#1877F2',
    fontSize: 18,
    lineHeight: '18px',
    fontFamily: 'MsAds MDL2 Assets',
    '&:before': {
      content: '"\\ECB3"',
    },
  },
});

const getRecommendationText = (recommendationType, i18n) => {
  if (recommendationType === POSITIVE_REVIEW_TYPE) {
    return i18n.getString(_TL_('recommends'));
  }
  return i18n.getString(_TL_('does not recommend'));
};

const UnStyledSmartReviewCard = ({
  item,
  classes,
  i18n,
  mode,
}) => {
  const isSmartPageImprovedReviewsEnabled = useIsSmartPageImprovedReviewsEnabled();
  const logger = useInstrumentationServiceContext();
  const { siteConfig } = useSiteConfig();
  const locale = useCurrentLocale();
  const {
    reviewText,
    recommendationType,
    reviewer = {},
    createdTime,
    type,
  } = item;
  const transformedCreateTime = transformDateWithLocaleForEditMode(createdTime, locale);
  const src = getImageUrlById(siteConfig, reviewer.imageId) || reviewer.picture;
  const {
    label: businessName,
  } = proxyReadLogoConfig({ i18n, siteConfig });

  const reviewSourceIcon = useMemo(() => {
    if (!isSmartPageImprovedReviewsEnabled) {
      return null;
    }

    if (type !== REVIEW_TYPE.CUSTOM_REVIEW) {
      return (<div className={classNames('review-source-icon-fb', classes.fbIcon)} />);
    }

    return null;
  }, [type, classes, isSmartPageImprovedReviewsEnabled]);

  useEffect(() => {
    if (logger) {
      logger.logTrace(`transformed CreateTime ${createdTime} to locale ${locale} string ${transformedCreateTime}`);
    }
  }, [createdTime, locale, logger, transformedCreateTime]);

  return (
    <div className={classes[mode]} >
      <div className="card" >
        <div className={classNames(classes.reviewHeadline, 'review-headline')}>
          {
            src && <Image
              src={src}
              alt=""
              className={classes.reviewerPhoto}
              height={40}
              width={40}
            />
          }
          {
            !src &&
            <CircleLogo
              name={reviewer.name ? removeStringEmoji(reviewer.name) : ''}
              className={classes.reviewerNameLogo}
            />
          }
          <div className={classes.headerContent}>
            <span className={classNames(classes.reviewerName, 'reviewer')} title={reviewer.name}>
              {reviewer.name}
            </span>
            {reviewer.name && ' '}
            <span className={classes.recommendationType}>
              {getRecommendationText(recommendationType, i18n)}
            </span>
            {' '}
            <span className={classNames(classes.businessName, 'businessName')} title={businessName}>
              {businessName}
            </span>
            {
              (type !== REVIEW_TYPE.CUSTOM_REVIEW) &&
              <span className={classNames(classes.createdTime, 'created-time')}>
                {transformedCreateTime}
              </span>
            }
          </div>
          {
            mode === REVIEW_CARD_MODE.EDIT_MODE && reviewSourceIcon
          }
        </div>
        <TooltipWrapper content={reviewText} lineClamp={4}>
          <span className={classNames(classes.reviewText, 'review-text')}>
            {reviewText}
          </span>
        </TooltipWrapper>
      </div>
    </div>
  );
};

UnStyledSmartReviewCard.propTypes = {
  item: PropTypes.shape({
    reviewText: PropTypes.string.isRequired,
    recommendationType: PropTypes.string.isRequired,
    reviewer: PropTypes.shape({
      name: PropTypes.string.isRequired,
      picture: PropTypes.string,
      imageId: PropTypes.string,
    }).isRequired,
    createdTime: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    reviewHeadline: PropTypes.string.isRequired,
    reviewerPhoto: PropTypes.string.isRequired,
    createdTime: PropTypes.string.isRequired,
    reviewText: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  mode: PropTypes.string,
};

UnStyledSmartReviewCard.defaultProps = {
  mode: REVIEW_CARD_MODE.EDIT_MODE,
};


export const SmartReviewCard = withDefaultStyles(
  UnStyledSmartReviewCard,
  getStyles
);
